import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LOGO_CIVISOFT, LOGO_GEO5 } from "../constants";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { genererQuestions, getSolCritique } from "../kb/agentQuestionsKB";
import { API_URL } from "../config";
import { genererQuestionsAdaptatives } from "../helpers/agentQuestionsAdaptatives";

const extraireReglesExclusion = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") return [data];
  if (Array.isArray(data.regles))  return data.regles;
  if (Array.isArray(data.motifs))  return data.motifs;
  if (Array.isArray(data.raisons)) return data.raisons;
  if (typeof data.regle  === "string") return [data.regle];
  if (typeof data.motif  === "string") return [data.motif];
  if (typeof data.raison === "string") return [data.raison];
  return Object.keys(data);
};

const getNomSolution = (id) => {
  const noms = {
    pieux_beton:               "Pieux Béton",
    pieux_fores:               "Pieux Forés",
    semelles_superficielles:   "Semelles Superficielles",
    radier_general:            "Radier Général",
    micropieux:                "Micropieux",
    longrines:                 "Longrines",
    palplanches:               "Rideau de Palplanches",
    massif_renforce:           "Massif Renforcé",
    stabilite_coins_roche:     "Stabilité des Coins de Roche",
    pieux_secants:             "Paroi de Pieux Sécants",
    paroi_moldee:              "Paroi Moulée",
    ecran_ancre:               "Écran Ancré",
    ecran_berlinois:           "Écran Berlinois",
    mur_cantilever:            "Mur Cantilever",
    gabions:                   "Gabions",
    colonnes_sol_ciment:       "Colonnes Sol-Ciment",
    colonnes_ballastees:       "Colonnes Ballastées",
  };
  return noms[id] || id.replace(/_/g, " ");
};

function TabAgentIA({ donneesProjet, profilUtilisateur, isAdmin, licence, pays, diagnosticBackend, solutionId, solutions, onAjoutDialogueRapport = () => {} }) {
  const isEU = licence === "eu";
  const _paysRaw = pays || donneesProjet?.clientInfo?.pays || "FR";
  const paysCode = _paysRaw === "france" ? "FR" : _paysRaw === "algerie" ? "DZ" : _paysRaw.toUpperCase() || "FR";
  const { t } = useTranslation();
  const [question, setQuestion] = useState("");
  const [historique, setHistorique] = useState([]);
  const [ajoutesIdx, setAjoutesIdx] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [statsSession, setStatsSession] = useState({ tokens: 0, cout: 0, questions: 0 });
  const [guideVisible, setGuideVisible] = useState(true);
  const [questionsContextuelles, setQuestionsContextuelles] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── Sélecteur de solution — 3 groupes sans doublons ──
  const idsVus = new Set();

  const solutionsClassees = (diagnosticBackend?.adequation?.classement || [])
    .map(s => { idsVus.add(s.id); return { id: s.id, rang: s.rang, niveau: s.niveau, statut: "classee" }; });

  const solutionsAutres = Object.entries(diagnosticBackend?.adequation?.solutions || {})
    .filter(([id]) => !idsVus.has(id))
    .map(([id, data]) => { idsVus.add(id); return { id, niveau: data.niveau, statut: "evaluee" }; });

  const solutionsExclues = Object.entries(diagnosticBackend?.eligibilite?.exclues || {})
    .filter(([id]) => !idsVus.has(id))
    .map(([id, data]) => { idsVus.add(id); return { id, regles: extraireReglesExclusion(data), statut: "exclue" }; });

  const getNiveauAdequation = (id) => {
    const sc = solutionsClassees.find(s => s.id === id);
    if (sc) return sc.niveau || null;
    const sa = solutionsAutres.find(s => s.id === id);
    if (sa) return sa.niveau || null;
    return null;
  };

  const [selectedSolutionId, setSelectedSolutionId] = useState(
    solutionId || diagnosticBackend?.adequation?.classement?.[0]?.id || null
  );

  // Reset conversation quand la solution sélectionnée change
  useEffect(() => { setHistorique([]); setErreur(null); setAjoutesIdx(new Set()); }, [selectedSolutionId]);

  // Garde-fou : réinitialise selectedSolutionId si le diagnostic change et l'id n'existe plus
  useEffect(() => {
    const tousLesIds = new Set([
      ...solutionsClassees.map(s => s.id),
      ...solutionsAutres.map(s => s.id),
      ...solutionsExclues.map(s => s.id),
    ]);
    if (selectedSolutionId && !tousLesIds.has(selectedSolutionId)) {
      setSelectedSolutionId(solutionsClassees[0]?.id || null);
    }
  }, [diagnosticBackend]); // eslint-disable-line react-hooks/exhaustive-deps

  // Régénère les questions KB dès que le projet ou ses solutions changent
  useEffect(() => {
    const sols = donneesProjet?.solutions;
    if (donneesProjet && sols?.length > 0) {
      // KB-UTIL-01 — solCritique pré-calculé avant transmission à genererQuestions
      const dpEnrichi = {
        ...donneesProjet,
        solCritique: getSolCritique(
          donneesProjet.couches || [],
          donneesProjet.typeSol || ""
        ),
      };
      setQuestionsContextuelles(genererQuestions(dpEnrichi, sols));
    } else {
      setQuestionsContextuelles([]);
    }
  }, [donneesProjet]);

  // ═══════════════════════════════════════════════════════
  // generateDynamicQuestions — PIVOT : solutions[0].id
  // La solution recommandée par le moteur dicte les questions.
  // Chaque question injecte : sol réel (stratigraphie), H, Zone EC8,
  // comparatif argumenté solutions[0] vs solutions[1].
  // ═══════════════════════════════════════════════════════
  const generateDynamicQuestions = (ctx) => {
    if (!ctx) return [
      "Comment calculer un pieu CPT en zone sismique 3 (EC8, NF P94-262) ?",
      "Quels modules GEO5 pour un immeuble R+8 sur argile — EC7 DA2* ?",
      "SF minimum pour talus en Zone 3 EC8 — méthode Bishop vs Spencer ?",
      "Gabions vs Mur Cantilever pour H=4m en Zone 2 EC8 — avantages et coûts ?",
      "Comment vérifier le glissement d'un mur cantilever H=5m avec bêche (NF P94-281) ?",
    ];

    // ── Extraction du contexte projet ───────────────────────────
    const { hauteur, typeSol, zoneSismique, espaceDisponible, nappe, longueur, couches, solutions, frottement } = ctx;

    const H    = parseFloat(hauteur) || 0;
    const zone = String(zoneSismique || "2");
    const L_ml = parseFloat(longueur) || 50;

    // Nature_Sol : stratigraphie réelle > typeSol > défaut
    const coucheFondation = couches?.find(c => c.nature && !["terre_vegetale","remblai"].includes(c.nature)) || couches?.[0];
    const Nature_Sol = coucheFondation?.nature || typeSol || "sable";

    // Paramètres mécaniques réels
    const phi_reel = parseFloat(coucheFondation?.phi || frottement) || null;
    const c_reel   = parseFloat(coucheFondation?.cohesion) || null;
    const gamma    = parseFloat(coucheFondation?.gamma) || null;
    const paramsStr = [
      phi_reel != null ? `φ=${phi_reel}°`   : null,
      c_reel   != null ? `c=${c_reel} kPa`  : null,
      gamma    != null ? `γ=${gamma} kN/m³` : null,
    ].filter(Boolean).join(", ");
    const paramsLabel = paramsStr ? ` (${paramsStr})` : "";

    // ── PIVOT : solutions recommandées par le moteur ─────────────
    const sol1Id = solutions?.[0]?.id || "";
    const sol2Id = solutions?.[1]?.id || "";
    const LABELS = {
      murs_cantilever:      "Mur Cantilever BA",
      deux_murs_cantilever: "Murs Cantilever étagés",
      mur_unique_hauteur:   "Mur Cantilever unique",
      gabions:              "Gabions",
      deux_murs_gabions:    "Gabions en gradins",
      ecrans:               "Écrans de soutènement",
      pentes_clouees:       "Pentes Clouées",
      talutage:             "Talutage",
      semelles:             "Semelles superficielles",
      pieux:                "Pieux forés",
      micropieux:           "Micropieux",
    };
    const Sol1 = LABELS[sol1Id] || "solution recommandée";
    const Sol2 = LABELS[sol2Id] || "";

    const Zone_Sismique  = `Zone ${zone} (EC8)`;
    const seismiqueEleve = ["3","4","5"].includes(zone);
    const nappeProbable  = ["haute","moderee","saisonniere"].includes(nappe);
    const B_min = +(H * 0.5).toFixed(1);
    const B_max = +(H * 0.7).toFixed(1);

    // ── Mapping Expert : une fonction par ID de solution ─────────
    const SOLUTION_QUESTIONS = {

      murs_cantilever: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — SF glissement comparé, coût €/ml et drainage ?`
          : `Pourquoi le ${Sol1} est-il recommandé pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} (EC7 DA2*) ?`,
        `Quels paramètres GEO5 Murs Cantilever pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} ? Semelle B=${B_min}–${B_max}m.`,
        `Faut-il une bêche sous le ${Sol1} H=${H}m en ${Zone_Sismique} sur ${Nature_Sol} — comment la positionner sous le voile dans GEO5 (NF P94-270 §7.3) ?`,
        `Ferraillage EC2 voile + semelle pour ${Sol1} H=${H}m sur ${Nature_Sol} — enrobage, As_min et fissuration wmax ?`,
        `SF glissement sismique ${Zone_Sismique} pour ${Sol1} H=${H}m sur ${Nature_Sol} — valeur minimale et solutions si insuffisant ?`,
      ],

      deux_murs_cantilever: () => {
        const phi   = phi_reel || 20;
        const L_inf = +(H / 2 / Math.tan(Math.PI / 4 + phi * Math.PI / 180 / 2)).toFixed(2);
        return [
          Sol2
            ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — SF glissement comparé, coût €/ml et mise en œuvre ?`
            : `Pourquoi 2 murs étagés de ${(H/2).toFixed(1)}m plutôt qu'un mur unique de ${H}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
          `Zone d'influence entre les 2 murs étagés : L=${L_inf}m pour φ=${phi}° sur ${Nature_Sol} — distance minimale à respecter (NF P94-281) ?`,
          `Paramètres EC7 DA2* à saisir dans GEO5 pour chaque mur de ${(H/2).toFixed(1)}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} ?`,
          `SF glissement sismique ${Zone_Sismique} pour chaque mur de ${(H/2).toFixed(1)}m sur ${Nature_Sol} — bêche nécessaire ?`,
          `Stabilité globale Bishop après les 2 murs étagés sur ${Nature_Sol} — SF ≥ 1.50 statique, SF ≥ 1.10 sismique (NF P94-270 §9) ?`,
        ];
      },

      mur_unique_hauteur: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — pourquoi le mur unique est-il déconseillé au-delà de 6m ?`
          : `Risques du mur cantilever unique H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — que préconise NF P94-270 ?`,
        `Si mur unique H=${H}m maintenu — semelle B=${+(H*0.8).toFixed(1)}m sur ${Nature_Sol}${paramsLabel} : SF glissement sismique en ${Zone_Sismique} ?`,
        `Quels paramètres GEO5 Murs Cantilever pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} ?`,
        `Ferraillage EC2 voile + semelle pour cantilever H=${H}m sur ${Nature_Sol} — armatures minimales et fissuration (NF EN 1992) ?`,
        `Stabilité globale Bishop après vérification interne du mur H=${H}m sur ${Nature_Sol} — SF minimum NF P94-270 §9 ?`,
      ],

      gabions: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — SF glissement comparé, drainage et coût €/m³ ?`
          : `Pourquoi les ${Sol1} sont-ils recommandés pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
        `Dimensions des blocs ${Sol1} pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — décalage, fruit et hauteur de bloc recommandés ?`,
        nappeProbable
          ? `Nappe présente → comment les ${Sol1} gèrent-ils la pression hydraulique sur ${Nature_Sol} (drainage naturel EC7) ?`
          : `${Sol1} en ${Zone_Sismique} sur ${Nature_Sol} : kh Mononobe-Okabe et SF glissement minimum (NF EN 1998-5) ?`,
        `Paramètres du remplissage pierres dans GEO5 Gabions pour ${Nature_Sol}${paramsLabel} — φ et γ recommandés NF P94 ?`,
        `Stabilité globale Bishop après vérification interne ${Sol1} H=${H}m sur ${Nature_Sol} — SF minimum NF P94-270 §9 ?`,
      ],

      deux_murs_gabions: () => {
        const phi   = phi_reel || 20;
        const L_inf = +(H / 2 / Math.tan(Math.PI / 4 + phi * Math.PI / 180 / 2)).toFixed(2);
        return [
          Sol2
            ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — coûts €/ml, SF glissement et drainage comparés ?`
            : `Pourquoi 2 murs gabions en gradins de ${(H/2).toFixed(1)}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
          `Zone d'influence entre les 2 gradins : L=${L_inf}m pour φ=${phi}° sur ${Nature_Sol} — distance minimale dans GEO5 ?`,
          `Dimensions et fruit des blocs gabions pour H/2=${(H/2).toFixed(1)}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} ?`,
          `SF glissement sismique ${Zone_Sismique} pour chaque gradin de ${(H/2).toFixed(1)}m sur ${Nature_Sol} — critique ou acceptable ?`,
          `Stabilité globale Bishop après les 2 gradins gabions sur ${Nature_Sol} — SF minimum NF P94-270 ?`,
        ];
      },

      pentes_clouees: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — SF global comparé, coût €/ml et mise en œuvre ?`
          : `Pourquoi les ${Sol1} sont-ils recommandés pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
        `Longueur et espacement des clous pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — méthode CLOUTERRE ?`,
        `SF global Bishop clouage pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — SF ≥ 1.50 statique, SF ≥ 1.10 sismique ?`,
        `Paramètres GEO5 Pentes Clouées pour ${Nature_Sol}${paramsLabel} — frottement sol-coulis et résistance structurelle clou ?`,
        `Parement pour les pentes clouées H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — béton projeté ou treillis soudé ?`,
      ],

      ecrans: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — déplacements, SF et coût €/ml comparés ?`
          : `Pourquoi un écran de soutènement est-il recommandé pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
        `Type d'écran pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — palplanches, berlinoise ou paroi moulée ?`,
        `Profondeur d'encastrement de l'écran H=${H}m sur ${Nature_Sol}${paramsLabel} — méthode DA2* NF P94-281 ?`,
        `Tirants d'ancrage ou butons pour l'écran H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — espacement et précontrainte ?`,
        `Vérification sismique de l'écran en ${Zone_Sismique} sur ${Nature_Sol} — kh Mononobe-Okabe NF EN 1998-5 §7.3 ?`,
      ],

      talutage: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — SF Bishop comparé, coût et entretien ?`
          : `Pourquoi le talutage est-il recommandé pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
        `Angle de talus admissible pour H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — méthode Bishop SF ≥ 1.50 ?`,
        `Végétalisation et confortement du talus H=${H}m sur ${Nature_Sol} — géogrilles ou ensemencement hydraulique ?`,
        `SF sismique minimum ${Zone_Sismique} pour le talus H=${H}m sur ${Nature_Sol} — Bishop avec kh Mononobe-Okabe ?`,
        nappeProbable
          ? `Nappe dans le talus sur ${Nature_Sol} → réduction SF de 15–25% — drainage à prévoir avant travaux ?`
          : `Drainage du talus H=${H}m sur ${Nature_Sol} — quand est-il indispensable (argile, limon, nappe saisonnière) ?`,
      ],

      semelles: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} pour ce projet sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — portance comparée EC7 DA2* ?`
          : `Pourquoi les ${Sol1} sont-elles recommandées sur ${Nature_Sol}${paramsLabel} (NF P94-261, EC7 DA2*) ?`,
        `Dimensions de semelle pour le bâtiment sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — tassement admissible 25mm NF P94-261 ?`,
        `Tassements prévisibles sur ${Nature_Sol}${paramsLabel} — méthode pressiométrique PMT dans GEO5 Tassements ?`,
        `Profondeur hors gel des semelles sur ${Nature_Sol} en France — DTU 13.1 + carte gel (hors gel à H0→H4) ?`,
        `Vérifications sismiques EC8 pour les semelles en ${Zone_Sismique} sur ${Nature_Sol} — NF EN 1998-5 §5 ?`,
      ],

      pieux: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — portance comparée NF P94-262 et coût €/ml ?`
          : `Pourquoi les ${Sol1} sont-ils recommandés sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} (NF P94-262) ?`,
        `Type de pieu pour ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — foré armé, micro-pieu ou CFA (NF P94-262) ?`,
        `Capacité portante d'un pieu foré sur ${Nature_Sol}${paramsLabel} — méthode PMT Ménard dans GEO5 (NF P94-262 Annexe A) ?`,
        `Profondeur d'ancrage dans la couche portante sur ${Nature_Sol} — 2× diamètre minimum NF P94-262 ?`,
        `Vérifications sismiques EC8 groupe de pieux en ${Zone_Sismique} sur ${Nature_Sol} — NF EN 1998-5 §5.4 ?`,
      ],

      pieux_secants: () => [
        `ISS-001 — Paroi de pieux sécants H=${H}m sur ${Nature_Sol}${paramsLabel} : comment justifier simultanément le soutenement ET la reprise des charges du bâtiment voisin (NF P94-262 + NF P94-270 §9) ?`,
        `Capacité portante axiale des pieux sécants sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — méthode DA2* NF P94-262, γRv=${1.4} ?`,
        `Stabilité Globale Bishop englobant paroi de pieux + bâtiment voisin sur ${Nature_Sol} — SF ≥ 1.50 statique, SF ≥ 1.10 sismique (NF P94-270 §9) ?`,
        `Déplacement latéral δH ≤ H/500 pour paroi de pieux H=${H}m sur ${Nature_Sol} — comment configurer le contrôle inclinométrique dans GEO5 Écrans ?`,
        `Mission G3 obligatoire (NF P94-500) pour paroi de pieux H=${H}m à proximité d'un bâtiment — quels instruments : inclinomètres, tassomètres, repères de nivellement ?`,
      ],

      micropieux: () => [
        Sol2
          ? `${Sol1} vs ${Sol2} sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — résistance axiale comparée, coût €/ml et délai ?`
          : `Pourquoi les micropieux sont-ils recommandés pour ce projet sur ${Nature_Sol}${paramsLabel} (NF P94-262 §14) ?`,
        `Capacité portante d'un micropieu sur ${Nature_Sol}${paramsLabel} — type IGU vs IRS, frottement latéral qs et résistance de pointe qb (NF P94-262) ?`,
        `Flambement des micropieux en ${Zone_Sismique} sur ${Nature_Sol} — longueur libre, diamètre et armature minimale (NF EN 1997-1 §7.6) ?`,
        `Séquence de forage micropieux en reprise sous-oeuvre sur ${Nature_Sol} — phases alternées et injection IRS (DTU 13.2) ?`,
        `Stabilité globale Bishop après mise en place des micropieux sur ${Nature_Sol} — SF ≥ 1.50 statique (NF P94-270 §9) ?`,
      ],

      tassements: () => [
        `Tassements prévisibles sur ${Nature_Sol}${paramsLabel} — méthode pressiométrique PMT Ménard dans GEO5 Tassements (NF P94-261 §6) ?`,
        `Tassement différentiel admissible pour ce bâtiment sur ${Nature_Sol} — DTU 13.1 : δmax=25mm absolu, Δδ=20mm différentiel ?`,
        `Consolidation oedométrique sur ${Nature_Sol}${paramsLabel} — coefficient Cv, durée de consolidation et tassement final (NF P94-261) ?`,
        `Méthode Peck pour les tassements induits par fouille H=${H}m sur ${Nature_Sol} — fuseau de tassements en surface ?`,
        `Vérifications ELS quasi-permanent sur ${Nature_Sol}${paramsLabel} — combinaisons EC7 et valeurs de tassement à justifier (NF P94-261 §6.3) ?`,
      ],

      stabilite_pentes: () => [
        Sol2
          ? `Stabilisation de pente H=${H}m sur ${Nature_Sol}${paramsLabel} — ${Sol1} vs ${Sol2} : SF Bishop comparé en ${Zone_Sismique} ?`
          : `Analyse de stabilité Bishop pour pente H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — SF ≥ 1.50 statique, SF ≥ 1.10 sismique ?`,
        `Méthode d'analyse recommandée pour ce talus sur ${Nature_Sol}${paramsLabel} — Bishop, Spencer ou Fellenius (NF P94-270 §9) ?`,
        `Effet de la nappe sur la stabilité du talus H=${H}m sur ${Nature_Sol} — réduction du SF et drainage à prévoir ?`,
        `Paramètres sismiques pour Bishop sur pente en ${Zone_Sismique} — coefficient kh Mononobe-Okabe (NF EN 1998-5 §7.3) ?`,
        `Solutions correctives si SF < 1.50 sur ${Nature_Sol}${paramsLabel} — clouage, drainage, géogrilles ou rechargement (NF P94-270) ?`,
      ],

      pentes_clouees_talus: () => [
        `Pentes Clouées pour stabilisation talus H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — longueur et espacement clous méthode CLOUTERRE ?`,
        `SF global Bishop après clouage du talus H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — SF ≥ 1.50 statique, SF ≥ 1.10 sismique ?`,
        `Résistance à l'arrachement des clous sur ${Nature_Sol}${paramsLabel} — frottement sol-coulis et diamètre de forage (NF P94-270) ?`,
        `Parement du talus cloué H=${H}m sur ${Nature_Sol} — béton projeté ou treillis soudé, épaisseur minimale ?`,
        `Drainage derrière le parement cloué sur ${Nature_Sol} — quand est-il obligatoire (argile, limon, nappe saisonnière) ?`,
      ],

      pieux_stab: () => [
        `Pieux stabilisateurs pour talus H=${H}m sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — espacement et diamètre optimal (NF P94-262) ?`,
        `Effort latéral repris par chaque pieu stabilisateur sur ${Nature_Sol}${paramsLabel} — méthode p-y dans GEO5 Pieux ?`,
        `SF global Bishop après mise en place des pieux stabilisateurs sur ${Nature_Sol} — SF ≥ 1.50 statique (NF P94-270 §9) ?`,
        `Profondeur d'ancrage des pieux stabilisateurs dans la couche stable sous ${Nature_Sol} — 2× la hauteur de la zone de glissement ?`,
        `Vérifications sismiques ${Zone_Sismique} pour pieux stabilisateurs sur ${Nature_Sol} — NF EN 1998-5 §5.4 ?`,
      ],
    };

    // Fallback si solution non reconnue ou projet non analysé
    const fallback = () => [
      `Étapes d'analyse GEO5 pour ce projet sur ${Nature_Sol}${paramsLabel} en ${Zone_Sismique} — EC7 DA2* ?`,
      Sol2
        ? `${Sol2} vs solution alternative pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} — comparatif SF et coût ?`
        : `Quels modules GEO5 sont nécessaires pour H=${H}m sur ${Nature_Sol} en ${Zone_Sismique} ?`,
      `Paramètres de sol à saisir dans GEO5 pour ${Nature_Sol}${paramsLabel} — NF P94 et EC7 DA2* ?`,
      `SF minimaux EC7 DA2* + EC8 pour ce type d'ouvrage sur ${Nature_Sol} en ${Zone_Sismique} ?`,
      `Comment intégrer ${Zone_Sismique} dans GEO5 pour ce projet (kh Mononobe-Okabe, NF EN 1998-5) ?`,
    ];

    const generator = SOLUTION_QUESTIONS[sol1Id] || fallback;
    return generator().slice(0, 5);
  };

  // Recalcul automatique à chaque re-render (donneesProjet change → React re-rend → questions régénérées)
  const SUGGESTIONS = generateDynamicQuestions(donneesProjet);

  // Questions adaptatives à la solution sélectionnée — priorité 1 dans l'affichage
  const questionsAdaptatives = useMemo(() => {
    if (!selectedSolutionId || !diagnosticBackend) return [];
    return genererQuestionsAdaptatives(selectedSolutionId, diagnosticBackend, donneesProjet);
  }, [selectedSolutionId, diagnosticBackend, donneesProjet]);

  // Construit un résumé projet + top 3 solutions pour enrichir le contexte IA
  const construireContexteProjet = () => {
    if (!donneesProjet) return null;
    const dp = donneesProjet;
    const H = parseFloat(dp.hauteur) || 0;
    const phi = parseFloat(dp.frottement) || 20;
    const estSoutenement = ["soutenement_remblai", "soutenement_deblai"].includes(dp.typeOuvrage);

    const couches = dp.couches || [];
    const cFond = couches.find(c => c.nature && !["terre_vegetale", "remblai"].includes(c.nature)) || couches[0];
    const phi_reel   = parseFloat(cFond?.frottement ?? cFond?.phi ?? dp.frottement) || null;
    const c_reel     = parseFloat(cFond?.cohesion) || null;
    const gamma_reel = parseFloat(cFond?.gamma) || null;
    const nature_reel = cFond?.nature || dp.typeSol || null;

    const sols = dp.solutions || [];
    let sol_rec = null;
    if (sols.length > 0) {
      sol_rec = sols.slice(0, 3)
        .map((s, i) => `#${i + 1} ${s.nom} (score ${s.score}%)`)
        .join(" | ");
    } else if (estSoutenement) {
      if (H > 6) {
        const L = +(H / 2 / Math.tan(Math.PI / 4 + phi * Math.PI / 180 / 2)).toFixed(2);
        sol_rec = `2 murs cantilever étagés de ${(H / 2).toFixed(1)}m — zone d'influence L=${L}m`;
      } else if (H >= 3) {
        sol_rec = `Mur Cantilever BA H=${H}m — semelle B=${(H * 0.5).toFixed(1)}–${(H * 0.7).toFixed(1)}m (EC7 DA2*)`;
      } else {
        sol_rec = `Mur Poids / Gabions H=${H}m — solution économique`;
      }
    }

    const SURCH_LABEL = { "0": "nulle", "10": "10 kN/m² (trafic léger)", "20": "20 kN/m² (trafic PL)", "30": "30 kN/m² (zone industrielle)", "50": "50 kN/m² (stockage lourd)" };

    return {
      type_ouvrage:         dp.typeOuvrage,
      hauteur_m:            dp.hauteur,
      longueur_m:           dp.longueur,
      nature_sol:           nature_reel,
      frottement_deg:       phi_reel,
      cohesion_kPa:         c_reel,
      poids_volumique_kNm3: gamma_reel,
      rdt_estime_kPa:       parseFloat(dp.rdt) || null,
      couches: couches.map((c, i) => ({
        num:             i + 1,
        nom:             c.nom || c.nature,
        nature:          c.nature,
        profondeur_toit: c.profondeur_toit ?? 0,
        profondeur_base: c.profondeur_base,
        gamma:           parseFloat(c.gamma) || null,
        phi:             parseFloat(c.frottement ?? c.phi) || null,
        cohesion:        parseFloat(c.cohesion) || null,
        N_spt:           parseInt(c.N_spt) || null,
      })),
      zone_sismique:        dp.zoneSismique,
      nappe:                dp.nappe,
      nappe_profondeur_m:   parseFloat(dp.nappeProf) || null,
      surcharge_kNm2:       SURCH_LABEL[dp.surcharge] || dp.surcharge || "non renseignée",
      espace_disponible:    dp.espaceDisponible,
      essais_disponibles:   dp.essais,
      source_parametres:    dp.essais?.includes("pressiometre")
                            ? `Essais réels — Pressiomètre (pl*=${dp.pl_etoile ?? "—"} kPa, EM=${dp.E_module ?? "—"} MPa)`
                            : dp.essais?.includes("cpt")
                            ? `Essais réels — CPT (qc=${dp.qc_cpt ?? "—"} MPa)`
                            : "Estimations théoriques — coefficients majorés (aucun essai in-situ)",
      batiment_voisin:      dp.batimentVoisin,
      batiment_voisin_distance_m: dp.batimentVoisinDistance,
      delai:                dp.delai,
      budget:               dp.budget,
      solutions_recommandees: sol_rec,
      norme_applicable:     "EC7 DA2* (France) · NF P94-270/261/262 · EC8 / NF EN 1998-1",
    };
  };

  const scrollBas = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollBas(); }, [historique, loading]);

  const poserQuestion = async (texte) => {
    const q = (texte || question).trim();
    if (!q) return;

    if (!diagnosticBackend || !selectedSolutionId) {
      setErreur("Lancez d'abord une analyse depuis l'onglet Solution pour activer l'agent.");
      return;
    }

    setGuideVisible(false);

    const nouveauMsg = { role: "user", content: q, ts: new Date() };
    setHistorique(prev => [...prev, nouveauMsg]);
    setQuestion("");
    setLoading(true);
    setErreur(null);

    try {
      const res = await fetch(`${API_URL}/agent/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          diagnostic:  diagnosticBackend,
          solution_id: selectedSolutionId,
          message:     q,
          history:     historique.slice(-6).map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (res.status === 400) {
        const err = await res.json();
        throw new Error(err.detail || "Requête invalide (400)");
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur serveur (${res.status})`);
      }

      const data = await res.json();
      const repMsg = {
        role:                    "assistant",
        content:                 data.reply,
        citations:               data.citations || [],
        modules:                 data.modules_geo5_referenced || [],
        is_technical:            data.is_technical || false,
        ts:                      new Date(),
      };

      setHistorique(prev => [...prev, repMsg]);
      setStatsSession(prev => ({ ...prev, questions: prev.questions + 1 }));
    } catch (e) {
      const msg = e.message || "";
      const isNetwork = msg.includes("Failed to fetch") || msg.includes("FailedToOpenSocket") || msg.includes("NetworkError") || msg.includes("Unable to connect");
      if (isNetwork) {
        setErreur("__network__");
      } else {
        setErreur(msg);
      }
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); poserQuestion(); }
  };

  // Formatage Markdown simple → HTML
  const formatReponse = (texte) => {
    // Traitement ligne par ligne pour éviter les <br/> en double
    // Pré-traitement : détecter et convertir les blocs tableau Markdown
    const lignes = texte.split('\n');
    const html = [];
    let i = 0;
    while (i < lignes.length) {
      // ── TABLEAU MARKDOWN ──
      // Détecter une ligne de tableau (commence par |)
      if (lignes[i].trim().startsWith('|') && lignes[i].includes('|', 1)) {
        const tableLines = [];
        while (i < lignes.length && lignes[i].trim().startsWith('|')) {
          tableLines.push(lignes[i]);
          i++;
        }
        // Filtrer la ligne de séparation (---|---)
        const dataLines = tableLines.filter(l => !l.match(/^\s*\|[-\s:|]+\|/));
        if (dataLines.length > 0) {
          let tableHtml = '<table style="width:100%;border-collapse:collapse;margin:8px 0;font-size:12px;">';
          dataLines.forEach((line, idx) => {
            const cells = line.split('|').filter((_, ci) => ci > 0 && ci < line.split('|').length - 1);
            const tag = idx === 0 ? 'th' : 'td';
            const rowBg = idx === 0 ? '#2D7A2D' : idx % 2 === 0 ? '#F5F8F5' : '#FFFFFF';
            const color = idx === 0 ? '#FFFFFF' : '#1A1A1A';
            tableHtml += `<tr style="background:${rowBg};">`;
            cells.forEach(cell => {
              const cellContent = fmt(cell.trim());
              tableHtml += `<${tag} style="padding:6px 10px;border:1px solid #D8E8D8;color:${color};text-align:left;font-weight:${idx===0?'700':'400'};">${cellContent}</${tag}>`;
            });
            tableHtml += '</tr>';
          });
          tableHtml += '</table>';
          html.push(tableHtml);
        }
        continue;
      }
      const l = lignes[i];
      // Sauter les lignes vides multiples (garder max 1 saut)
      if (l.trim() === '') {
        if (i > 0 && lignes[i-1].trim() !== '') html.push('<div style="height:4px"></div>');
        i++; continue;
      }
      // Titres
      if (l.startsWith('### ')) { html.push(`<div style="color:#2D7A2D;font-size:13px;font-weight:700;margin:10px 0 3px;border-bottom:1px solid #D8E8D8;padding-bottom:3px;">${l.slice(4)}</div>`); i++; continue; }
      if (l.startsWith('## '))  { html.push(`<div style="color:#1A1A1A;font-size:15px;font-weight:800;margin:12px 0 4px;">${l.slice(3)}</div>`); i++; continue; }
      if (l.startsWith('# '))   { html.push(`<div style="color:#2D7A2D;font-size:16px;font-weight:900;margin:0 0 8px;">${l.slice(2)}</div>`); i++; continue; }
      // Listes
      if (l.startsWith('- ') || l.startsWith('* ')) {
        html.push(`<div style="display:flex;gap:6px;margin:1px 0;"><span style="color:#00A651;margin-top:1px;flex-shrink:0;">▸</span><span>${fmt(l.slice(2))}</span></div>`);
        i++; continue;
      }
      const numMatch = l.match(/^(\d+)\. (.+)$/);
      if (numMatch) {
        html.push(`<div style="display:flex;gap:6px;margin:1px 0;"><span style="color:#2D7A2D;font-weight:700;min-width:16px;flex-shrink:0;">${numMatch[1]}.</span><span>${fmt(numMatch[2])}</span></div>`);
        i++; continue;
      }
      // Alertes
      if (l.includes('⚠') || l.startsWith('⚠️')) {
        html.push(`<div style="background:#FFF8E1;border-left:3px solid #F59E0B;padding:6px 10px;border-radius:0 6px 6px 0;margin:6px 0;font-size:12px;color:#92400E;">${fmt(l)}</div>`);
        i++; continue;
      }
      if (l.includes('✅')) {
        html.push(`<div style="background:#F0FDF4;border-left:3px solid #16A34A;padding:6px 10px;border-radius:0 6px 6px 0;margin:6px 0;font-size:12px;color:#15803D;">${fmt(l)}</div>`);
        i++; continue;
      }
      // Paragraphe normal
      html.push(`<div style="margin:1px 0;line-height:1.45;">${fmt(l)}</div>`);
      i++;
    }
    return html.join('');
  };

  // Formatage inline (gras, code)
  const fmt = (t) => t
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#1A1A1A;font-weight:700;">$1</strong>')
    .replace(/`(.+?)`/g, '<code style="background:#E8F5E9;color:#1B5E20;padding:1px 4px;border-radius:3px;font-size:11px;font-family:monospace;">$1</code>');


  // ─── Génération rapport PDF ───────────────────
  const generatePDF = (mode = 'visualiser') => {
    if (historique.length === 0) return;

    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { year:'numeric', month:'long', day:'numeric' });
    const heureStr = now.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
    const nomUser = profilUtilisateur ? `${profilUtilisateur.prenom} ${profilUtilisateur.nom}` : 'Ingénieur';
    const orgUser = profilUtilisateur?.organisation || "Bureau d'études";

    // Construire le HTML du rapport
    const conversations = historique.map((msg, i) => {
      if (msg.role === 'user') {
        return `<div class="question">
          <div class="q-label">❓ Question ${Math.ceil((i+1)/2)}</div>
          <div class="q-text">${msg.content}</div>
        </div>`;
      } else {
        // Conversion Markdown → HTML pour le PDF (avec tableaux)
        const mdToHtml = (texte) => {
          const lines = texte.split('\n');
          const out = [];
          let i = 0;
          while (i < lines.length) {
            const l = lines[i];
            // Tableau Markdown
            if (l.trim().startsWith('|') && l.includes('|', 1)) {
              const tLines = [];
              while (i < lines.length && lines[i].trim().startsWith('|')) {
                tLines.push(lines[i]); i++;
              }
              const dataLines = tLines.filter(ln => !ln.match(/^\s*\|[-\s:|]+\|/));
              if (dataLines.length > 0) {
                let t = '<table style="width:100%;border-collapse:collapse;margin:8px 0;font-size:10pt;">';
                dataLines.forEach((ln, idx) => {
                  const cells = ln.split('|').filter((_, ci) => ci > 0 && ci < ln.split('|').length - 1);
                  const tag = idx === 0 ? 'th' : 'td';
                  const bg = idx === 0 ? '#2D7A2D' : idx % 2 === 0 ? '#F5F8F5' : '#fff';
                  const col = idx === 0 ? '#fff' : '#1a1a1a';
                  t += '<tr style="background:' + bg + ';">';
                  cells.forEach(c => {
                    t += '<' + tag + ' style="padding:5px 8px;border:1px solid #D8E8D8;color:' + col + ';text-align:left;font-weight:' + (idx===0?'700':'400') + ';">' + c.trim().replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>') + '</' + tag + '>';
                  });
                  t += '</tr>';
                });
                t += '</table>';
                out.push(t);
              }
              continue;
            }
            if (l.trim() === '') { out.push('<br>'); i++; continue; }
            if (l.startsWith('### ')) { out.push('<h4>' + l.slice(4) + '</h4>'); i++; continue; }
            if (l.startsWith('## '))  { out.push('<h3>' + l.slice(3) + '</h3>'); i++; continue; }
            if (l.startsWith('# '))   { out.push('<h2>' + l.slice(2) + '</h2>'); i++; continue; }
            if (l.startsWith('- ') || l.startsWith('* ')) { out.push('<li>' + l.slice(2) + '</li>'); i++; continue; }
            const nm = l.match(/^(\d+)\. (.+)$/);
            if (nm) { out.push('<li><strong>' + nm[1] + '.</strong> ' + nm[2] + '</li>'); i++; continue; }
            if (l.includes('⚠')) { out.push('<div class="alerte">' + l + '</div>'); i++; continue; }
            if (l.includes('✅')) { out.push('<div class="ok">' + l + '</div>'); i++; continue; }
            out.push('<p style="margin:2px 0;">' + l + '</p>');
            i++;
          }
          return out.join('\n')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/`(.+?)`/g, '<code style="background:#E8F5E9;color:#1B5E20;padding:1px 3px;border-radius:2px;font-family:monospace;">$1</code>');
        };
        const reponseHTML = mdToHtml(msg.content);
        return `<div class="reponse">
          <div class="r-label">🤖 Réponse de l'Analyse IA GEO5</div>
          <div class="r-text">${reponseHTML}</div>
          ${msg.modules?.length > 0 ? `<div class="modules">Modules GEO5 : ${msg.modules.join(' · ')}</div>` : ''}
          ${msg.citations?.length > 0 ? `<div class="modules">Normes citées : ${msg.citations.join(' · ')}</div>` : ''}
        </div>`;
      }
    }).join('');

    const projetInfo = donneesProjet ? `
      <table class="info-table">
        <tr><td>Type d'ouvrage</td><td><strong>${{
          'soutenement_remblai':'Soutènement de remblai',
          'soutenement_deblai':'Soutènement de déblai / fouille',
          'talus_stabilisation':'Stabilisation de talus',
          'fondation_batiment':'Fondation de bâtiment',
          'infrastructure_lineaire':'Infrastructure linéaire',
          'ouvrage_art':"Ouvrage d\\'art"
        }[donneesProjet.typeOuvrage] || donneesProjet.typeOuvrage}</strong></td></tr>
        ${donneesProjet.hauteur ? `<tr><td>Hauteur / Profondeur</td><td><strong>${donneesProjet.hauteur} m</strong></td></tr>` : ''}
        ${donneesProjet.longueur ? `<tr><td>Longueur linéaire</td><td><strong>${donneesProjet.longueur} m</strong></td></tr>` : ''}
        ${donneesProjet.typeSol ? `<tr><td>Nature du sol</td><td><strong>${donneesProjet.typeSol}</strong></td></tr>` : ''}
        ${donneesProjet.cohesion ? `<tr><td>Cohésion c</td><td><strong>${donneesProjet.cohesion} kPa</strong></td></tr>` : ''}
        ${donneesProjet.frottement ? `<tr><td>Angle de frottement φ</td><td><strong>${donneesProjet.frottement}°</strong></td></tr>` : ''}
        ${donneesProjet.nappe ? `<tr><td>Nappe phréatique</td><td><strong>${donneesProjet.nappe}</strong></td></tr>` : ''}
        ${donneesProjet.zoneSismique ? `<tr><td>Zone sismique (EC8)</td><td><strong>Zone ${donneesProjet.zoneSismique}</strong></td></tr>` : ''}
        ${donneesProjet.espaceDisponible ? `<tr><td>Espace disponible</td><td><strong>${donneesProjet.espaceDisponible}</strong></td></tr>` : ''}
      </table>` : '<p><em>Aucune donnée de projet saisie.</em></p>';

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Rapport GEO5 Expert Advisor — ${dateStr}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #1a1a1a; background: #fff; }
  .page { max-width: 210mm; margin: 0 auto; padding: 15mm 18mm; }

  /* EN-TÊTE */
  .header { border-bottom: 3px solid #2D7A2D; padding-bottom: 12px; margin-bottom: 20px; }
  .header-top { display: flex; justify-content: space-between; align-items: flex-start; }
  .app-name { font-size: 20pt; font-weight: 900; color: #2D7A2D; }
  .app-sub { font-size: 9pt; color: #555; margin-top: 3px; }
  .badge { background: #2D7A2D; color: white; font-size: 8pt; font-weight: 700;
           padding: 4px 10px; border-radius: 4px; text-align: center; }
  .badge-sub { font-size: 7pt; color: #a5d6a7; margin-top: 2px; }

  /* MÉTA */
  .meta { background: #F5F8F5; border: 1px solid #D8E8D8; border-radius: 6px;
          padding: 10px 14px; margin-bottom: 18px; font-size: 9pt; color: #555; }
  .meta strong { color: #1a1a1a; }

  /* SECTIONS */
  .section-title { font-size: 12pt; font-weight: 800; color: #2D7A2D;
    background: #E8F5E9; border-left: 4px solid #2D7A2D;
    padding: 6px 12px; margin: 18px 0 10px; }
  .info-table { width: 100%; border-collapse: collapse; font-size: 10pt; }
  .info-table td { padding: 5px 8px; border-bottom: 1px solid #eee; }
  .info-table td:first-child { color: #555; width: 45%; }

  /* CONVERSATIONS */
  .question { background: #E8F5E9; border-radius: 0 8px 8px 8px;
              padding: 10px 14px; margin: 12px 0 4px; }
  .q-label { font-size: 8pt; font-weight: 700; color: #2D7A2D;
              text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
  .q-text { font-size: 10.5pt; color: #1a1a1a; line-height: 1.4; }

  .reponse { background: #fff; border: 1px solid #D8E8D8;
             border-radius: 8px 0 8px 8px; padding: 10px 14px; margin: 4px 0 14px; }
  .r-label { font-size: 8pt; font-weight: 700; color: #555;
              text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
  .r-text { font-size: 10pt; color: #1a1a1a; line-height: 1.5; }
  .r-text h2 { font-size: 12pt; color: #2D7A2D; margin: 8px 0 4px; }
  .r-text h3 { font-size: 11pt; color: #1a1a1a; margin: 6px 0 3px; }
  .r-text h4 { font-size: 10pt; color: #2D7A2D; margin: 5px 0 2px; border-bottom: 1px solid #eee; padding-bottom: 2px; }
  .r-text li { margin-left: 18px; margin-top: 2px; }
  .r-text code { background: #E8F5E9; color: #1B5E20; padding: 1px 4px;
                 border-radius: 3px; font-size: 9pt; font-family: monospace; }
  .alerte { background: #FFF8E1; border-left: 3px solid #F59E0B;
            padding: 6px 10px; margin: 6px 0; font-size: 9.5pt; color: #92400E; }
  .ok { background: #F0FDF4; border-left: 3px solid #16A34A;
        padding: 6px 10px; margin: 6px 0; font-size: 9.5pt; color: #15803D; }
  table { width:100%; border-collapse:collapse; margin:8px 0; font-size:10pt; }
  th, td { padding:5px 8px; border:1px solid #D8E8D8; text-align:left; }
  th { background:#2D7A2D; color:#fff; font-weight:700; }
  tr:nth-child(even) { background:#F5F8F5; }
  .modules { margin-top: 8px; font-size: 8pt; color: #2D7A2D;
             border-top: 1px solid #E8F5E9; padding-top: 6px; font-style: italic; }

  /* FOOTER */
  .footer { border-top: 2px solid #2D7A2D; margin-top: 24px; padding-top: 10px;
            font-size: 8pt; color: #555; display: flex; justify-content: space-between; }
  .footer strong { color: #2D7A2D; }

  .disclaimer { background: #FFFDE7; border: 1px solid #FDD835;
                border-radius: 6px; padding: 10px 14px; margin-top: 16px;
                font-size: 9pt; color: #5D4037; line-height: 1.5; }

  @media print {
    .page { padding: 10mm 15mm; }
    .question, .reponse { break-inside: avoid; }
  }
</style>
</head>
<body>
<div class="page">

  <!-- EN-TÊTE -->
  <div class="header">
    <div class="header-top">
      <div>
        <div class="app-name">GEO5 Expert Advisor</div>
        <div class="app-sub">by CIVISOFT — Distributeur Exclusif GEO5 en France</div>
      </div>
      <div>
        <div class="badge">Partenaire GEO5 Autorisé</div>
        <div class="badge-sub" style="text-align:center;color:#555;font-size:8pt;margin-top:2px;">Fine Software s.r.o.</div>
      </div>
    </div>
  </div>

  <!-- MÉTA -->
  <div class="meta">
    <strong>Rapport de consultation IA géotechnique</strong> — Généré le ${dateStr} à ${heureStr}<br>
    Ingénieur : <strong>${nomUser}</strong> — ${orgUser}<br>
    Référentiels appliqués : <strong>EC8 (NF EN 1998-5) · Eurocode 7 (NF EN 1997-1) · Eurocode 2 (NF EN 1992-1-1) · NF P94</strong>
  </div>

  <!-- DONNÉES DU PROJET -->
  <div class="section-title">📋 Données du Projet</div>
  ${projetInfo}

  <!-- CONSULTATION IA -->
  <div class="section-title">💬 Consultation Analyse IA — ${Math.floor(historique.length/2)} question(s)</div>
  ${conversations}

  <!-- DISCLAIMER -->
  <div class="disclaimer">
    <strong>⚠ Note importante :</strong><br>
    Ce rapport a été généré par GEO5 Expert Advisor, assistant IA géotechnique de Civisoft, sur la base
    de la base de connaissance GEO5 et des normes en vigueur (EC8, Eurocode 7, Eurocode 2).
    Les recommandations de cet outil constituent une aide à la décision et doivent être confirmées
    par un bureau d'études géotechnique agréé et validées par les calculs justificatifs GEO5 complets.
    Ce document accompagne (et ne remplace pas) la note de calcul officielle.
  </div>

  <!-- FOOTER -->
  <div class="footer">
    <div><strong>CIVISOFT</strong> — contact@civisoft-fr.com — +33 (0)1 XX XX XX XX — www.civisoft-fr.com</div>
    <div>GEO5 Expert Advisor v2.1 — ${dateStr}</div>
  </div>

</div>
</body>
</html>`;

    const dateFile = new Date().toISOString().slice(0, 10);
    const fileName = `Rapport_GEO5_${dateFile}.pdf`;

    const genererPDF = async (contenuHTML) => {
      // Créer un iframe caché pour rendre le HTML
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:960px;height:auto;border:none;';
      document.body.appendChild(iframe);
      iframe.contentDocument.open();
      iframe.contentDocument.write(contenuHTML);
      iframe.contentDocument.close();

      await new Promise(r => setTimeout(r, 800));

      const body = iframe.contentDocument.body;
      const canvas = await html2canvas(body, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 960,
        windowWidth: 960,
      });
      document.body.removeChild(iframe);

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      let posY = 0;

      while (posY < imgH) {
        pdf.addImage(imgData, 'JPEG', 0, -posY, pageW, imgH);
        posY += pageH;
        if (posY < imgH) pdf.addPage();
      }

      pdf.save(fileName);
    };

    if (mode === 'telecharger') {
      // Téléchargement PDF direct
      genererPDF(html);
    } else {
      // Visualiser dans un nouvel onglet avec boutons PDF + Imprimer
      const win = window.open('', '_blank', 'width=960,height=800');
      win.document.write(html);
      win.document.close();
      win.onload = () => {
        const btn = win.document.createElement('div');
        btn.innerHTML =
          '<div style="position:fixed;top:12px;right:12px;display:flex;gap:8px;z-index:9999;">' +
          '<button onclick="window.print()" style="background:#2D7A2D;color:#fff;border:none;padding:8px 18px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;">🖨️ Imprimer / PDF</button>' +
          '<button onclick="window.close()" style="background:#eee;color:#333;border:none;padding:8px 14px;border-radius:6px;font-size:13px;cursor:pointer;">✕ Fermer</button>' +
          '</div>';
        win.document.body.appendChild(btn);
      };
    }
  };

  // Palette Fine/GEO5
  const C = {
    vert:       "#2D7A2D",
    vertClair:  "#00A651",
    vertLight:  "#E8F5E9",
    vertMid:    "#A5D6A7",
    dark:       "#1A1A1A",
    mid:        "#555555",
    light:      "#F5F8F5",
    border:     "#D8E8D8",
    white:      "#FFFFFF",
    // aliases pour la rétrocompatibilité
    orange:     "#2D7A2D",
    orangeLight:"#E8F5E9",
    orangeMid:  "#A5D6A7",
  };

  return (
    <div style={{ fontFamily:"'Segoe UI', system-ui, sans-serif" }}>
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* ── EN-TÊTE AGENT ── */}
      <div style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: "20px 24px",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)"
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{
            width: 52, height: 52,
            background: `linear-gradient(135deg, ${C.vert}, ${C.vertClair})`,
            borderRadius: 14,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 26, boxShadow: `0 4px 16px ${C.orange}40`
          }}>🤖</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.dark }}>Analyse IA Géotechnique</div>
            {donneesProjet ? (
              <div style={{ marginTop:3 }}>
                <span style={{ fontSize:11, background:C.orange, color:"#fff", borderRadius:4, padding:"2px 8px", fontWeight:700, letterSpacing:0.5 }}>
                  📋 PROJET EN COURS
                </span>
                <div style={{ fontSize:12, color:C.mid, marginTop:4 }}>
                  <strong style={{ color:C.dark }}>
                    {{"soutenement_remblai":"Soutènement remblai","soutenement_deblai":"Soutènement déblai","talus_stabilisation":"Stabilisation talus","fondation_batiment":"Fondation bâtiment","infrastructure_lineaire":"Infrastructure linéaire","ouvrage_art":"Ouvrage d'art"}[donneesProjet.typeOuvrage] || donneesProjet.typeOuvrage}
                  </strong>
                  {donneesProjet.hauteur && ` · H = ${donneesProjet.hauteur} m`}
                  {donneesProjet.typeSol && ` · ${donneesProjet.typeSol}`}
                  {donneesProjet.zoneSismique && ` · Zone ${donneesProjet.zoneSismique}`}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: C.mid, marginTop: 2 }}>
                Basé sur la KB GEO5 · 24 modules · Eurocode 7 · EC8 (NF EN 1998-5) · Contexte France
              </div>
            )}
          </div>
        </div>


        {/* Boutons actions */}
        <div style={{ display:"flex", gap:8 }}>
          {historique.length > 0 && (
            <>
              <button
                onClick={() => generatePDF('visualiser')}
                title="Visualiser le rapport dans un nouvel onglet"
                style={{
                  background:`linear-gradient(135deg, ${C.vert}, ${C.vertClair})`,
                  border:"none", color:"#fff", borderRadius:8,
                  padding:"7px 14px", cursor:"pointer", fontSize:12, fontWeight:700,
                  display:"flex", alignItems:"center", gap:5,
                  boxShadow:`0 2px 8px ${C.vert}40`
                }}>
                👁️ Voir Rapport Comparatif
              </button>
              <button
                onClick={() => generatePDF('telecharger')}
                title="Télécharger le rapport"
                style={{
                  background:"transparent",
                  border:`1.5px solid ${C.vert}`, color:C.vert, borderRadius:8,
                  padding:"7px 14px", cursor:"pointer", fontSize:12, fontWeight:700,
                  display:"flex", alignItems:"center", gap:5
                }}>
                ⬇️ Télécharger
              </button>
            </>
          )}
          {historique.length > 0 && (
            <button onClick={() => { setHistorique([]); setStatsSession({ tokens:0, cout:0, questions:0 }); setErreur(null); }}
              style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.mid, borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:12 }}>
              🔄 Nouvelle conversation
            </button>
          )}
        </div>
      </div>

      {/* ── SÉLECTEUR DE SOLUTION — 3 groupes ── */}
      {diagnosticBackend && (solutionsClassees.length > 0 || solutionsAutres.length > 0 || solutionsExclues.length > 0) && (
        <div style={{
          background: C.white, border: `1px solid ${C.border}`,
          borderRadius: 14, padding: "14px 18px", marginBottom: 18,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.vert, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
            🎯 Sélectionner une solution à analyser
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

            {/* Groupe 1 — Solutions classées */}
            {solutionsClassees.length > 0 && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.vert, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                  Solutions classées
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {solutionsClassees.map(sol => {
                    const sel = selectedSolutionId === sol.id;
                    const niv = sol.niveau?.toUpperCase() || "";
                    const nivBg  = niv.includes("TRES") || niv.includes("TRÈS") ? "#DCFCE7"
                                 : niv.includes("RESERVE") || niv.includes("RÉSERVE") ? "#FEF9C3"
                                 : niv.includes("ACCEPTABLE") ? "#FEF3C7" : C.vertLight;
                    const nivCol = niv.includes("TRES") || niv.includes("TRÈS") ? "#166534"
                                 : niv.includes("RESERVE") || niv.includes("RÉSERVE") ? "#713F12"
                                 : niv.includes("ACCEPTABLE") ? "#92400E" : C.vert;
                    return (
                      <button key={sol.id} onClick={() => setSelectedSolutionId(sol.id)} style={{
                        background: sel ? `linear-gradient(135deg, ${C.vert}, ${C.vertClair})` : C.light,
                        border: sel ? `1.5px solid ${C.vert}` : `1px solid ${C.border}`,
                        borderRadius: 7, padding: "6px 11px", textAlign: "left",
                        cursor: "pointer", fontSize: 12, transition: "all 0.15s",
                        color: sel ? "#fff" : C.dark, fontWeight: sel ? 700 : 400,
                      }}>
                        <span style={{ opacity: 0.75 }}>#{sol.rang || "?"} </span>
                        <strong>{getNomSolution(sol.id)}</strong>
                        {sol.niveau && (
                          <span style={{
                            marginLeft: 6, fontSize: 10, borderRadius: 4, padding: "1px 6px", fontWeight: 600,
                            background: sel ? "rgba(255,255,255,0.25)" : nivBg,
                            color: sel ? "#fff" : nivCol,
                          }}>
                            {sol.niveau}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Séparateur 1|2 */}
            {solutionsClassees.length > 0 && solutionsAutres.length > 0 && (
              <div style={{ width: 1, background: C.border, flexShrink: 0, alignSelf: "stretch" }} />
            )}

            {/* Groupe 2 — Autres évaluées */}
            {solutionsAutres.length > 0 && (
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.mid, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                  Autres évaluées
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {solutionsAutres.map(sol => {
                    const sel = selectedSolutionId === sol.id;
                    return (
                      <button key={sol.id} onClick={() => setSelectedSolutionId(sol.id)} style={{
                        background: sel ? "#EFF6FF" : C.white,
                        border: sel ? "1.5px solid #3B82F6" : `1px solid ${C.border}`,
                        borderRadius: 7, padding: "6px 11px", textAlign: "left",
                        cursor: "pointer", fontSize: 12, transition: "all 0.15s",
                        color: C.dark, fontWeight: sel ? 700 : 400,
                      }}>
                        <strong>{getNomSolution(sol.id)}</strong>
                        {sol.niveau && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: C.mid, fontWeight: 600 }}>
                            {sol.niveau}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Séparateur 2|3 */}
            {(solutionsClassees.length > 0 || solutionsAutres.length > 0) && solutionsExclues.length > 0 && (
              <div style={{ width: 1, background: C.border, flexShrink: 0, alignSelf: "stretch" }} />
            )}

            {/* Groupe 3 — Solutions écartées */}
            {solutionsExclues.length > 0 && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                  Solutions écartées
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {solutionsExclues.map(sol => {
                    const sel = selectedSolutionId === sol.id;
                    return (
                      <button key={sol.id} onClick={() => setSelectedSolutionId(sol.id)} style={{
                        background: sel ? "#FEF3C7" : C.white,
                        border: sel ? "1.5px solid #F59E0B" : `1px solid ${C.border}`,
                        borderRadius: 7, padding: "6px 11px", textAlign: "left",
                        cursor: "pointer", fontSize: 12, transition: "all 0.15s",
                        color: C.dark, fontWeight: sel ? 700 : 400,
                      }}>
                        <span style={{ marginRight: 4 }}>⛔</span>
                        <strong>{getNomSolution(sol.id)}</strong>
                        {sol.regles?.length > 0 && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: "#92400E", fontWeight: 600 }}>
                            {sol.regles.slice(0, 3).join(", ")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ZONE PRINCIPALE ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:20, alignItems:"start" }}>

        {/* COLONNE CHAT */}
        <div style={{
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          display: "flex", flexDirection: "column",
          minHeight: 520,
        }}>

          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", padding:"20px", minHeight:400, maxHeight:520 }}>

            {/* Message de bienvenue */}
            {historique.length === 0 && (
              <div style={{ textAlign:"center", padding:"40px 20px" }}>
                <div style={{ fontSize:48, marginBottom:16 }}>🏗️</div>
                <div style={{ fontSize:18, fontWeight:700, color:C.dark, marginBottom:8 }}>
                  GEO5 Expert Advisor
                </div>
                {donneesProjet ? (
                  <div style={{ fontSize:13, color:C.mid, lineHeight:1.9, maxWidth:460, margin:"0 auto" }}>
                    Votre projet a été analysé. Je connais :<br/>
                    <span style={{ display:"inline-block", background:C.orangeLight, border:`1px solid ${C.orangeMid}`, borderRadius:6, padding:"6px 14px", margin:"8px 0", fontSize:12, color:C.orange, fontWeight:700 }}>
                      {{"soutenement_remblai":"Soutènement de remblai","soutenement_deblai":"Fouille/déblai","talus_stabilisation":"Stabilisation de talus","fondation_batiment":"Fondation bâtiment","infrastructure_lineaire":"Infrastructure","ouvrage_art":"Ouvrage d'art"}[donneesProjet.typeOuvrage]}
                      {donneesProjet.hauteur && ` · H=${donneesProjet.hauteur}m`}
                      {donneesProjet.typeSol && ` · ${donneesProjet.typeSol}`}
                      {donneesProjet.zoneSismique && ` · Zone ${donneesProjet.zoneSismique}`}
                    </span><br/>
                    Cliquez sur une question ci-dessous ou posez la vôtre.
                  </div>
                ) : (
                  <div style={{ fontSize:14, color:C.mid, lineHeight:1.7, maxWidth:400, margin:"0 auto" }}>
                    Posez votre question géotechnique.<br/>
                    L'agent consulte la base de connaissance GEO5 et répond avec des valeurs numériques contextualisées pour la France.
                  </div>
                )}
                <div style={{ display:"inline-block", background: diagnosticBackend && selectedSolutionId ? C.orangeLight : "#FEF3C7", border:`1px solid ${diagnosticBackend && selectedSolutionId ? C.orangeMid : "#FCD34D"}`, borderRadius:8, padding:"8px 16px", fontSize:12, color: diagnosticBackend && selectedSolutionId ? C.orange : "#92400E", fontWeight:600, marginTop:16 }}>
                  {diagnosticBackend && selectedSolutionId
                    ? "✅ Diagnostic chargé — agent prêt"
                    : "⚠️ Lancez une analyse (onglet Solution) pour activer l'agent"}
                </div>

                {/* Message bienvenue contextualisé selon la solution sélectionnée */}
                {selectedSolutionId && diagnosticBackend && (() => {
                  const sc = solutionsClassees.find(s => s.id === selectedSolutionId);
                  const se = solutionsExclues.find(s => s.id === selectedSolutionId);
                  if (sc) return (
                    <div style={{ background: C.vertLight, border: `1px solid ${C.vertMid}`, borderRadius: 10, padding: "12px 16px", marginTop: 14, maxWidth: 440, margin: "14px auto 0", textAlign: "left" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.vert, marginBottom: 4 }}>Vous analysez maintenant :</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: C.dark }}>
                        {getNomSolution(sc.id)}
                        {sc.niveau && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: C.vert, background: C.white, border: `1px solid ${C.vertMid}`, borderRadius: 4, padding: "1px 7px" }}>{sc.niveau}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: C.mid, marginTop: 6 }}>Posez vos questions.</div>
                    </div>
                  );
                  if (se) return (
                    <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 10, padding: "12px 16px", marginTop: 14, maxWidth: 440, margin: "14px auto 0", textAlign: "left" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>Solution écartée :</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: C.dark }}>{getNomSolution(se.id)}</div>
                      {se.regles?.length > 0 && <div style={{ fontSize: 12, color: "#92400E", marginTop: 4 }}>Motifs : {se.regles.join(", ")}</div>}
                      <div style={{ fontSize: 12, color: C.mid, marginTop: 6 }}>Posez vos questions sur cette exclusion.</div>
                    </div>
                  );
                  return null;
                })()}

                {/* ── Bulles guide — disparaissent dès la 1ère question ── */}
                {guideVisible && (
                  <div style={{ marginTop:32, display:"flex", flexDirection:"column", gap:12, alignItems:"center" }}>
                    {/* Bulle 1 — Questions préparées */}
                    <div style={{ display:"flex", alignItems:"center", gap:10, background:"#F0FDF4", border:"1.5px dashed #4CAF50", borderRadius:12, padding:"10px 18px", maxWidth:380, animation:"fadeIn 0.5s ease" }}>
                      <span style={{ fontSize:20 }}>👉</span>
                      <span style={{ fontSize:13, color:"#2D7A2D", lineHeight:1.5 }}>
                        <strong>Questions préparées</strong> — cliquez sur l'une des questions à droite, adaptées à votre projet.
                      </span>
                      <span style={{ fontSize:18, marginLeft:4 }}>➡️</span>
                    </div>
                    {/* Bulle 2 — Saisie libre */}
                    <div style={{ display:"flex", alignItems:"center", gap:10, background:"#EFF6FF", border:"1.5px dashed #3B82F6", borderRadius:12, padding:"10px 18px", maxWidth:380, animation:"fadeIn 0.8s ease" }}>
                      <span style={{ fontSize:20 }}>⬇️</span>
                      <span style={{ fontSize:13, color:"#1E40AF", lineHeight:1.5 }}>
                        <strong>Question libre</strong> — tapez votre propre question géotechnique dans le champ ci-dessous.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Historique */}
            {historique.map((msg, i) => (
              <div key={i} style={{
                display:"flex",
                flexDirection: msg.role === "user" ? "row-reverse" : "row",
                gap:10, marginBottom:16, alignItems:"flex-start"
              }}>
                {/* Avatar */}
                <div style={{
                  width:32, height:32, borderRadius:"50%", flexShrink:0,
                  background: msg.role === "user"
                    ? "linear-gradient(135deg, #2563eb, #1d4ed8)"
                    : `linear-gradient(135deg, ${C.orange}, #d04a0a)`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, color:"#fff", fontWeight:700
                }}>
                  {msg.role === "user" ? "👤" : "🤖"}
                </div>

                {/* Bulle */}
                <div style={{
                  maxWidth:"78%",
                  background: msg.role === "user" ? C.vertLight : C.white,
                  border: msg.role === "user" ? `1px solid ${C.vertMid}` : `1px solid ${C.border}`,
                  borderRadius: msg.role === "user" ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                  padding:"12px 16px",
                  boxShadow:"0 1px 4px rgba(0,0,0,0.06)"
                }}>
                  {msg.role === "user" ? (
                    <div style={{ fontSize:14, color:C.dark, lineHeight:1.6 }}>{msg.content}</div>
                  ) : (
                    <div
                      style={{ fontSize:13, color:C.dark, lineHeight:1.7 }}
                      dangerouslySetInnerHTML={{ __html: formatReponse(msg.content) }}
                    />
                  )}

                  {/* Badge analyse technique */}
                  {msg.is_technical && (
                    <div style={{ marginTop:8, display:"inline-block", background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:4, padding:"2px 8px", fontSize:10, color:"#1D4ED8", fontWeight:700 }}>
                      🔬 Analyse géotechnique
                    </div>
                  )}

                  {/* Citations normatives */}
                  {msg.citations?.length > 0 && (
                    <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:4 }}>
                      {msg.citations.map((c,j) => (
                        <span key={j} style={{ background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:99, padding:"2px 8px", fontSize:10, color:"#92400E", fontWeight:600 }}>
                          {c}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Modules GEO5 référencés */}
                  {msg.modules?.length > 0 && (
                    <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:4, borderTop:`1px solid ${C.border}`, paddingTop:8 }}>
                      <span style={{ fontSize:10, color:C.mid, marginRight:4, fontWeight:600 }}>GEO5 :</span>
                      {msg.modules.map((m,j) => (
                        <span key={j} style={{ background:C.vertLight, border:`1px solid ${C.vertMid}`, borderRadius:99, padding:"2px 8px", fontSize:10, color:C.vert, fontWeight:600 }}>
                          {m}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div style={{ marginTop:6, fontSize:10, color:"#aaa" }}>
                    {msg.ts?.toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" })}
                  </div>

                  {/* Bouton "Ajouter au rapport" — uniquement messages assistant */}
                  {msg.role === "assistant" && (() => {
                    const dejaAjoute = ajoutesIdx.has(i);
                    const limitAtteinte = (donneesProjet?.dialogueExpertRapport?.length || 0) >= 3;
                    const desactive = limitAtteinte && !dejaAjoute;
                    return (
                      <div style={{ marginTop:8, display:"flex", justifyContent:"flex-end" }}>
                        <button
                          disabled={dejaAjoute || desactive}
                          onClick={() => {
                            if (dejaAjoute || desactive) return;
                            onAjoutDialogueRapport({
                              solutionId:       selectedSolutionId,
                              solutionNom:      getNomSolution(selectedSolutionId),
                              niveauAdequation: getNiveauAdequation(selectedSolutionId),
                              question:         historique[i - 1]?.content || '',
                              reponse:          msg.content,
                              dateAjout:        new Date().toISOString(),
                            });
                            setAjoutesIdx(prev => new Set([...prev, i]));
                          }}
                          style={{
                            fontSize:11, padding:"3px 10px", borderRadius:6, border:"1px solid",
                            cursor: dejaAjoute || desactive ? "default" : "pointer",
                            opacity: desactive ? 0.45 : 1,
                            background: dejaAjoute ? "#ECFDF5" : "#FFF7ED",
                            borderColor: dejaAjoute ? "#6EE7B7" : C.orange,
                            color: dejaAjoute ? "#059669" : C.orange,
                            fontWeight:600,
                            transition:"opacity 0.2s",
                          }}
                        >
                          {dejaAjoute ? "✓ Ajouté" : "📋 Ajouter au rapport"}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}

            {/* Indicateur de frappe */}
            {loading && (
              <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:16 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:`linear-gradient(135deg,${C.orange},#d04a0a)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🤖</div>
                <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:"4px 14px 14px 14px", padding:"12px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    {[0,1,2].map(j => (
                      <div key={j} style={{
                        width:7, height:7, borderRadius:"50%", background:C.orange,
                        animation:"pulse 1.2s ease-in-out infinite",
                        animationDelay:`${j*0.2}s`, opacity:0.7
                      }}/>
                    ))}
                    <span style={{ fontSize:12, color:C.mid, marginLeft:6 }}>L'agent réfléchit...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Erreur */}
            {erreur && (
              erreur === "__network__" ? (
                <div style={{ background:"#1e1b4b", border:"1px solid #4338ca", borderRadius:12, padding:"16px 18px", marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    <span style={{ fontSize:20 }}>🔌</span>
                    <strong style={{ fontSize:14, color:"#a5b4fc" }}>
                      {isEU ? "AI server starting up…" : "Serveur IA en démarrage…"}
                    </strong>
                  </div>
                  <div style={{ fontSize:13, color:"#818cf8", lineHeight:1.6, marginBottom:12 }}>
                    {isEU
                      ? "The AI server goes to sleep after inactivity. It usually takes 15–30 seconds to restart. Please retry in a moment."
                      : "Le serveur IA se met en veille après inactivité. Il redémarre en 15–30 secondes. Réessayez dans un instant."}
                  </div>
                  <button
                    onClick={() => { setErreur(null); const last = historique.filter(m => m.role === "user").slice(-1)[0]; if (last) poserQuestion(last.content); }}
                    style={{ background:"#4338ca", color:"#fff", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer" }}
                  >
                    {isEU ? "🔄 Retry" : "🔄 Réessayer"}
                  </button>
                </div>
              ) : (
                <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"12px 16px", marginBottom:12, fontSize:13, color:"#dc2626" }}>
                  ⚠️ <strong>{isEU ? "Error:" : "Erreur :"}</strong> {erreur}
                </div>
              )
            )}

            <div ref={messagesEndRef}/>
          </div>

          {/* Zone de saisie */}
          <div style={{ borderTop:`1px solid ${C.border}`, padding:"16px 20px", background:C.light }}>
            <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
              <textarea
                ref={inputRef}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("agent_tab.placeholder")}
                rows={2}
                style={{
                  flex:1, padding:"10px 14px",
                  border:`1.5px solid ${C.border}`,
                  borderRadius:10, fontSize:14, resize:"none",
                  outline:"none", fontFamily:"inherit", lineHeight:1.5,
                  background:C.white, color:C.dark,
                  transition:"border 0.2s"
                }}
                onFocus={e => e.target.style.borderColor = C.orange}
                onBlur={e => e.target.style.borderColor = C.border}
              />
              <button
                onClick={() => poserQuestion()}
                disabled={loading || !question.trim()}
                style={{
                  background: loading || !question.trim()
                    ? "#e5e7eb"
                    : `linear-gradient(135deg, ${C.orange}, #d04a0a)`,
                  color: loading || !question.trim() ? "#9ca3af" : "#fff",
                  border:"none", borderRadius:10,
                  padding:"10px 20px", cursor: loading || !question.trim() ? "default" : "pointer",
                  fontSize:14, fontWeight:700, whiteSpace:"nowrap",
                  boxShadow: loading || !question.trim() ? "none" : `0 4px 14px ${C.orange}40`,
                  transition:"all 0.2s"
                }}
              >
                {loading ? "⏳" : t("agent_tab.envoyer")}
              </button>
            </div>
            <div style={{ fontSize:11, color:C.mid, marginTop:8 }}>
              ↵ Entrée pour envoyer · Shift+Entrée pour nouvelle ligne
            </div>
          </div>
        </div>

        {/* COLONNE DROITE — Suggestions + Infos */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Suggestions — adaptatives (priorité 1), KB (priorité 2), génériques (fallback) */}
          {(() => {
            const questionsToShow = questionsAdaptatives.length > 0
              ? questionsAdaptatives
              : questionsContextuelles.length > 0
                ? questionsContextuelles
                : SUGGESTIONS;
            const isAdaptatif = questionsAdaptatives.length > 0;
            const isKB        = !isAdaptatif && questionsContextuelles.length > 0;
            const nomSolution  = isAdaptatif && selectedSolutionId
              ? (diagnosticBackend?.adequation?.solutions?.[selectedSolutionId]?.nom || getNomSolution(selectedSolutionId))
              : null;

            return (
              <div style={{
                background:C.white, border:`1px solid ${C.border}`,
                borderRadius:14, overflow:"hidden",
                boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
              }}>
                <div style={{
                  background:`linear-gradient(135deg, ${C.vert}, ${C.vertClair})`,
                  padding:"12px 16px", color:"#fff", fontSize:12, fontWeight:700, letterSpacing:1
                }}>
                  {isAdaptatif
                    ? `🎯 ${nomSolution || "SOLUTION SÉLECTIONNÉE"}`
                    : isKB
                      ? "🎯 QUESTIONS ADAPTÉES AU PROJET"
                      : donneesProjet
                        ? "💡 QUESTIONS SUR VOTRE PROJET"
                        : "💡 QUESTIONS FRÉQUENTES"}
                </div>
                {isAdaptatif && (
                  <div style={{ background:"#F0FDF4", borderBottom:`1px solid ${C.border}`, padding:"6px 14px", fontSize:10, color:C.vert, fontWeight:600 }}>
                    ✅ Questions adaptées à la solution sélectionnée
                  </div>
                )}
                {!isAdaptatif && isKB && (
                  <div style={{ background:"#F0FDF4", borderBottom:`1px solid ${C.border}`, padding:"6px 14px", fontSize:10, color:C.vert, fontWeight:600 }}>
                    ✅ Générées par le Système Expert — {donneesProjet?.solutions?.length || 0} solution(s) analysée(s)
                  </div>
                )}
                <div style={{ padding:"8px 0" }}>
                  {questionsToShow.map((q, i) => {
                    const texte = typeof q === "string" ? q : q.texte;
                    const key   = typeof q === "string" ? `q-${i}` : (q.id || `q-${i}`);
                    return (
                      <button key={key} onClick={() => poserQuestion(texte)} style={{
                        width:"100%", background:"transparent",
                        border:"none", borderBottom: i < questionsToShow.length - 1 ? `1px solid ${C.border}` : "none",
                        padding:"10px 14px", textAlign:"left", cursor:"pointer",
                        fontSize:12, color:C.dark, lineHeight:1.5,
                        transition:"background 0.15s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = C.vertLight}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <span style={{ color: isAdaptatif ? "#00A651" : isKB ? "#00A651" : C.vert, fontWeight:700, marginRight:6 }}>
                          {(isAdaptatif || isKB) ? "◆" : "▸"}
                        </span>
                        {texte}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Info KB */}
          <div style={{
            background:C.white, border:`1px solid ${C.border}`,
            borderRadius:14, padding:"14px 16px",
            boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
          }}>
            <div style={{ fontSize:11, color:C.orange, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>
              📚 Base de connaissance
            </div>
            {[
              ["Modules GEO5", "24 modules JSON"],
              ["Manuels intégrés", "N°1–19 (FR)"],
              ["Notes de calcul", "Pieux CPT + autres"],
              ["Normes", "EC7 + EC8 (NF EN 1998)"],
              ["Moteur IA", "GEO5 Expert Advisor v2"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${C.border}`, fontSize:12 }}>
                <span style={{ color:C.mid }}>{k}</span>
                <span style={{ color:C.dark, fontWeight:600 }}>{v}</span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Animation dots */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// COMPOSANT CO-PILOTE GEO5
// =============================================================================


export default TabAgentIA;
